// src/components/CookieBanner.jsx
//
// Slim cookie consent. Uses sessionStorage so it shows on every
// new browser session unless the visitor permanently accepted.
//
// Policy references now link directly to the canonical pages
// (/privacy, /terms, /disclaimer) instead of an inline modal that
// could drift out of sync with Legal.jsx. Links open in a new tab
// so the visitor doesn't lose their place on the page.
//
import React, { useState } from 'react';
import { Cookie, X } from 'lucide-react';

const SESSION_KEY = 'odftrades_cookies_shown';
const POLICY_KEY  = 'odftrades_cookies_accepted';

const LINK_STYLE = {
  color: '#00e5a0',
  textDecoration: 'none',
  fontWeight: 500,
};

export default function CookieBanner() {
  const [visible, setVisible] = useState(() => {
    try {
      // Show every new session UNLESS they permanently accepted
      if (localStorage.getItem(POLICY_KEY) === 'true') return false;
      return sessionStorage.getItem(SESSION_KEY) !== 'true';
    } catch { return true; }
  });

  if (!visible) return null;

  const accept = () => {
    try {
      localStorage.setItem(POLICY_KEY, 'true');
      sessionStorage.setItem(SESSION_KEY, 'true');
    } catch {}
    setVisible(false);
  };

  const decline = () => {
    try { sessionStorage.setItem(SESSION_KEY, 'true'); } catch {}
    setVisible(false);
  };

  return (
    <div
      style={{
        position: 'fixed', bottom: 20, left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9998, width: 'calc(100% - 40px)', maxWidth: 560,
        background: '#16161f', border: '1px solid #2a2a3e',
        borderRadius: 14, padding: '18px 20px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'flex-start', gap: 14,
        animation: 'fadeIn 0.3s ease',
      }}
    >
      <Cookie size={20} style={{ color: '#00e5a0', flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#f0f0f5', marginBottom: 4 }}>
          We use cookies
        </div>
        <div style={{ fontSize: 12, color: '#8888a8', lineHeight: 1.65, marginBottom: 14 }}>
          ODFTrades — the educational trading platform and software company founded by ODF — uses
          cookies to save your preferences and dashboard data. We don't use cookies for advertising or
          third-party tracking. See our{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>Privacy Policy</a>
          {' '}for details.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={accept}
            style={{
              background: '#00e5a0', color: '#0a0a0f', border: 'none',
              borderRadius: 7, padding: '8px 18px', fontSize: 12,
              fontWeight: 600, cursor: 'pointer',
              fontFamily: "'IBM Plex Sans', sans-serif",
            }}
          >
            Accept Cookies
          </button>
          <button
            onClick={decline}
            style={{
              background: 'transparent', color: '#8888a8',
              border: '1px solid #2a2a3e', borderRadius: 7,
              padding: '8px 18px', fontSize: 12, cursor: 'pointer',
              fontFamily: "'IBM Plex Sans', sans-serif",
            }}
          >
            Decline
          </button>
        </div>
      </div>
      <button
        onClick={decline}
        aria-label="Dismiss"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#55556a', padding: 0, flexShrink: 0,
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
